import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';

import { Link } from '@bibliocommons/base-links';
import { ModalHeader, ModalContent } from '@bibliocommons/modal';
import UserAvatar from '@bibliocommons/user-avatar';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { TextButton } from '@bibliocommons/base-buttons';
import { ChevronDown } from '@bibliocommons/base-icons';
import Context from '@bibliocommons/context';

import { TableStencil } from 'app/components/shared/Table';

import { selectAllContentLikes } from 'app/selectors/ugc/LikesSelector';
import { selectUserEntities } from 'app/selectors/EntitiesSelector';

import './UgcLikesModal.scss';

export default function UgcLikesModal({ contentId, count, likesActions }) {
  const { mobileApp: isMobileApp } = useContext(Context);
  const likes = useSelector(selectAllContentLikes).filter(like => like.get('contentId') === contentId);
  const users = useSelector(selectUserEntities);
  const isLoading = useSelector(state => state.getIn(['ugc', 'likes', 'isFetching'], false));
  const [displayedLikes, setDisplayedLikes] = useState(Immutable.List());
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!likes.isSubset(displayedLikes)) {
      setDisplayedLikes(page > 1 ? displayedLikes.concat(likes) : likes);
    }
  }, [likes, displayedLikes, setDisplayedLikes, page]);

  useEffect(() => {
    likesActions.getLikes({ contentId, page });
  }, [page, contentId, likesActions]);

  function renderLike(like) {
    const owner = users.get(like.get('ownerId').toString());
    return (
      <li className="ugc-likes-user" key={like.get('id')}>
        <UserAvatar hideLink={isMobileApp} userIdentity={owner} size="xs" />
        {isMobileApp ? (
          <>
            <span aria-hidden>{owner.get('name')}</span>
            <ScreenReaderMessage>
              <FormattedMessage id="sr_liked_ugc_user" values={{ username: owner.get('name') }} />
            </ScreenReaderMessage>
          </>
        ) : (
          <Link href={`/user_profile/${owner.get('id')}`} dataKey="ugc-likes-owner">
            <span aria-hidden>{owner.get('name')}</span>
            <ScreenReaderMessage>
              <FormattedMessage id="sr_liked_ugc_user" values={{ username: owner.get('name') }} />
            </ScreenReaderMessage>
          </Link>
        )}
      </li>
    );
  }

  function renderShowMoreButton() {
    return (
      displayedLikes.size < count && (
        <TextButton
          handleClick={() => setPage(page + 1)}
          className="show-more-likes-button"
          dataKey="show-more-likes-button"
          isLoading={isLoading}
          size="small"
        >
          <span>
            <span aria-hidden>
              <FormattedMessage id="button_show_more" />
            </span>
            <ScreenReaderMessage>
              <FormattedMessage id="button_show_more_liked_ugc_users" />
            </ScreenReaderMessage>
          </span>
          <ChevronDown />
        </TextButton>
      )
    );
  }

  return (
    <div className="cp-ugc-likes-modal">
      <ModalHeader title={<FormattedMessage id="likes_count" values={{ count }} />} />
      <ModalContent>
        {page === 1 && isLoading ? (
          <TableStencil />
        ) : (
          <>
            <ul className="likes-list">{displayedLikes.map(like => renderLike(like))}</ul>
            {renderShowMoreButton()}
          </>
        )}
      </ModalContent>
    </div>
  );
}

UgcLikesModal.propTypes = {
  contentId: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  likesActions: PropTypes.objectOf(PropTypes.func).isRequired
};
